import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Route } from '@/routes/_app.ads.accounts.index';
import {
  useAccountList,
  useCreateAccount,
  useOperatorOptions,
  useRechargeAccount,
  useAssignOperator,
  useClearOperatorAssignment,
  useAccountApps,
  useBatchBanAccounts,
  useBatchZeroAccounts,
} from '@/lib/api/accounts-queries';
import { useHasPermission } from '@/lib/hooks/use-auth';
import { useAdPlatformList } from '@/lib/api/ad-platforms-queries';
import { useAdProviderList } from '@/lib/api/ad-providers-queries';
import { useToast } from '@/lib/hooks/use-toast';
import type { AccountStatus, AdsAccount } from '@/lib/api/types';
import type { BatchAccountStatusResult } from '@/lib/api/accounts';
import { AccountListView } from './list-page.view';
import { CreateAccountDialog } from './create-account-dialog';
import { BatchCreateDrawer } from './batch-create-drawer';
import { BatchStatusDialog, type BatchStatusAction } from './batch-status-dialog';

export function AccountListPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [createOpen, setCreateOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  // 当前批量模式：null 时列表无勾选列；点顶部「批量封禁/清零」进入
  const [batchMode, setBatchMode] = useState<BatchStatusAction | null>(null);
  // 跨页累计的选中账户。存整行而非 id：翻页后行数据已不在 data 里，批量弹窗的状态预检还要用
  const [selected, setSelected] = useState<Map<string, AdsAccount>>(new Map());
  // 确认弹窗是否打开（选择阶段不弹，点「确认」才弹）
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [batchResult, setBatchResult] = useState<BatchAccountStatusResult | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (
      search.createTimeStart ||
      search.createTimeEnd ||
      search.updateTimeStart ||
      search.updateTimeEnd
    ) {
      void navigate({
        search: (prev) => ({
          ...prev,
          createTimeStart: undefined,
          createTimeEnd: undefined,
          updateTimeStart: undefined,
          updateTimeEnd: undefined,
        }),
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page = search.page ?? 1;

  const { data, isLoading, error, refetch } = useAccountList({
    pageNo: page,
    pageSize: 20,
    accountCode: search.search,
    status: (['0', '1', '2'] as const).includes(String(search.status) as '0' | '1' | '2')
      ? (Number(search.status) as AccountStatus)
      : undefined,
    platformId: search.platformId,
    providerId: search.providerId,
    accountName: search.accountName,
    operatorId: search.operatorId,
    pkg: search.pkg,
    createTimeStart: search.createTimeStart,
    createTimeEnd: search.createTimeEnd,
    updateTimeStart: search.updateTimeStart,
    updateTimeEnd: search.updateTimeEnd,
  });

  const canCreate = useHasPermission('api:ads-admin:ad-account:create');
  const canBan = useHasPermission('api:ads-admin:ad-account:ban');
  const canZero = useHasPermission('api:ads-admin:ad-account:zero');
  const canRecharge = useHasPermission('api:ads-admin:ad-account:recharge');
  const canAssign = useHasPermission('api:ads-admin:ad-account:assign-operator');
  const canReportConsume = useHasPermission('api:ads-admin:ad-account-transaction:create');
  const canManageBinding = useHasPermission('api:ads-admin:ad-account:assign-app');
  const { data: operators = [] } = useOperatorOptions({ enabled: canAssign });
  const { data: platforms } = useAdPlatformList({ pageNo: 1, pageSize: 100, status: 1 });
  const { data: providers } = useAdProviderList({ pageNo: 1, pageSize: 100, status: 1 });
  const { data: appsData } = useAccountApps({ enabled: true });

  const createAccount = useCreateAccount();
  const recharge = useRechargeAccount();
  const assign = useAssignOperator();
  const clearAssign = useClearOperatorAssignment();
  const batchBan = useBatchBanAccounts();
  const batchZero = useBatchZeroAccounts();
  const batchMutation = batchMode === 'zero' ? batchZero : batchBan;

  const handleFilterChange = (f: {
    search?: string;
    status?: '0' | '1' | '2';
    platformId?: number;
    providerId?: number;
    accountName?: string;
    operatorId?: number;
    pkg?: string;
    createTimeStart?: string;
    createTimeEnd?: string;
    updateTimeStart?: string;
    updateTimeEnd?: string;
  }) => {
    // 「已选 N」的心智绑定在当前结果集上，换了筛选条件就退出批量、清空选中
    exitBatch();
    void navigate({ search: (prev) => ({ ...prev, ...f, page: undefined }) });
  };

  const handlePageChange = (p: number) => {
    void navigate({ search: (prev) => ({ ...prev, page: p }) });
  };

  const startBatch = (action: BatchStatusAction) => {
    // 切换动作时清空选中：封禁/清零的可操作状态不同，沿用旧选择会误导预检
    if (action !== batchMode) setSelected(new Map());
    setBatchMode(action);
  };

  const exitBatch = () => {
    setBatchMode(null);
    setSelected(new Map());
    setConfirmOpen(false);
    setBatchResult(null);
    batchBan.reset();
    batchZero.reset();
  };

  const toggleSelect = (account: AdsAccount, checked: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (checked) next.set(account.accountId, account);
      else next.delete(account.accountId);
      return next;
    });
  };

  const togglePage = (accounts: AdsAccount[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      accounts.forEach((account) => {
        if (checked) next.set(account.accountId, account);
        else next.delete(account.accountId);
      });
      return next;
    });
  };

  // 关结果弹窗：回到选择阶段（失败项仍选中），不退出批量模式
  const closeDialog = () => {
    setConfirmOpen(false);
    setBatchResult(null);
    batchBan.reset();
    batchZero.reset();
  };

  const onBatchSuccess = (verb: string) => (result: BatchAccountStatusResult) => {
    // 成功的移出选中，失败的留着让用户修正后重试
    setSelected((prev) => {
      const next = new Map(prev);
      result.succeeded.forEach((accountId) => next.delete(accountId));
      return next;
    });
    if (result.errors.length > 0) {
      setBatchResult(result);
      return;
    }
    exitBatch();
    toast({ description: `已${verb} ${result.succeeded.length} 个账户` });
  };

  const runBatch = (accountIds: string[], banDate?: string) => {
    if (batchMode === 'ban') {
      batchBan.mutate({ accountIds, banDate }, { onSuccess: onBatchSuccess('封禁') });
    } else if (batchMode === 'zero') {
      batchZero.mutate({ accountIds }, { onSuccess: onBatchSuccess('清零') });
    }
  };

  return (
    <>
      <div className="mx-auto flex h-full w-full max-w-[1760px] flex-col">
        <AccountListView
          data={data?.records}
          total={data?.total ?? 0}
          page={page}
          pageSize={20}
          isLoading={isLoading}
          error={error}
          filters={{
            search: search.search,
            status: search.status,
            platformId: search.platformId,
            providerId: search.providerId,
            accountName: search.accountName,
            operatorId: search.operatorId,
            pkg: search.pkg,
            createTimeStart: search.createTimeStart,
            createTimeEnd: search.createTimeEnd,
            updateTimeStart: search.updateTimeStart,
            updateTimeEnd: search.updateTimeEnd,
          }}
          canRecharge={canRecharge}
          canAssign={canAssign}
          canReportConsume={canReportConsume}
          canManageBinding={canManageBinding}
          canBan={canBan}
          canZero={canZero}
          batchMode={batchMode}
          selected={selected}
          operators={operators}
          platforms={platforms?.records ?? []}
          providers={providers?.records ?? []}
          apps={appsData ?? []}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          onCreateAccount={canCreate ? () => setCreateOpen(true) : undefined}
          onBatchCreate={canCreate ? () => setBatchOpen(true) : undefined}
          onViewDetail={(accountId) =>
            void navigate({ to: '/ads/accounts/$id', params: { id: accountId } })
          }
          onRecharge={(accountId, amount, requestId) =>
            recharge.mutate(
              { accountId, amount, requestId },
              { onError: (err) => toast({ variant: 'destructive', description: err.message }) },
            )
          }
          onAssign={(accountId, operatorId) =>
            assign.mutate(
              { accountId, assignedOperatorId: operatorId },
              { onError: (err) => toast({ variant: 'destructive', description: err.message }) },
            )
          }
          onClearAssign={(accountId) =>
            clearAssign.mutate(
              { accountId },
              { onError: (err) => toast({ variant: 'destructive', description: err.message }) },
            )
          }
          onToggleSelect={toggleSelect}
          onTogglePage={togglePage}
          onStartBatch={startBatch}
          onCancelBatch={exitBatch}
          onConfirmBatch={() => setConfirmOpen(true)}
          onRetry={() => void refetch()}
        />
      </div>
      <CreateAccountDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(req) => createAccount.mutate(req, { onSuccess: () => setCreateOpen(false) })}
        isPending={createAccount.isPending}
        error={createAccount.error}
      />
      <BatchCreateDrawer open={batchOpen} onClose={() => setBatchOpen(false)} />
      <BatchStatusDialog
        action={confirmOpen ? batchMode : null}
        accounts={[...selected.values()]}
        isPending={batchMutation.isPending}
        error={batchMutation.error}
        result={batchResult}
        onClose={closeDialog}
        onConfirm={runBatch}
      />
    </>
  );
}
